import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * Handles the return from Emergent Google OAuth. Detects `session_id` in the URL
 * fragment (or query), exchanges it for a session_token via the backend, and
 * lands the user at /account.
 */
const AuthCallback = () => {
  const { exchangeGoogleSession } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const readSessionId = () => {
      const hash = window.location.hash || "";
      const cleanHash = hash.startsWith("#") ? hash.slice(1) : hash;
      const hashParams = new URLSearchParams(cleanHash);
      const fromHash = hashParams.get("session_id");
      if (fromHash) return fromHash;
      const search = new URLSearchParams(window.location.search || "");
      return search.get("session_id");
    };

    const sessionId = readSessionId();
    if (!sessionId) {
      setError("No session information found. Please try signing in again.");
      return;
    }

    (async () => {
      const { ok, error } = await exchangeGoogleSession(sessionId);
      // Clear the URL fragment before navigating away
      window.history.replaceState(null, "", window.location.pathname);
      if (!ok) {
        setError(error);
        return;
      }
      navigate("/account", { replace: true });
    })();
  }, [exchangeGoogleSession, navigate]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="auth-callback-page">
      {error ? (
        <div className="text-center">
          <p className="font-display text-xl text-rose-600">Sign-in failed</p>
          <p className="mt-2 text-sm text-slate-500 max-w-md">{error}</p>
          <button
            onClick={() => navigate("/login", { replace: true })}
            className="mt-6 text-brand text-sm font-semibold hover:underline"
            data-testid="auth-callback-retry-btn"
          >
            Return to log in
          </button>
        </div>
      ) : (
        <div className="text-center">
          <div className="mx-auto w-10 h-10 rounded-full border-2 border-brand border-t-transparent animate-spin" />
          <p className="mt-4 text-sm text-slate-500">Completing sign-in…</p>
        </div>
      )}
    </div>
  );
};

export default AuthCallback;
